import {
  AccountingTypeLabelModifier,
  accountingType,
} from '@/features/master-table/models';
import {
  type ReportOrganizingTableFormat5Model,
  type ReportOrganizingTableFormat5SummaryModel,
  format5ColumnType,
} from '@/features/mold/models/organizing-table';
import type {
  projectDetailUrlTypeEnum,
  reportFileType,
} from '@/features/mold/models/report';
import { getProjectDetailPageUrl } from '@/features/project/_utils/project';
import {
  SkipAdministrativeReformTeamEvaluationReasonLabelModifier,
  type skipAdministrativeReformTeamEvaluationReason,
} from '@/features/project/projects/_models/project';
import { t } from '@/i18n';
import { SPACE_TEXT, createCsv, formatAmountReport } from '@/utils';
import type { z } from 'zod';

type ReportDetailsType = z.infer<typeof ReportOrganizingTableFormat5Model>;
type ReportSummaryType = z.infer<
  typeof ReportOrganizingTableFormat5SummaryModel
>;

type FileType = z.infer<typeof reportFileType>;

type MoneyConvertParams = {
  value: number | null;
  type: FileType;
};

type SummaryConvertParams = {
  values: ReportSummaryType;
  fileType: FileType;
};

export const format5HeaderModifier = ({
  currentYear,
  isCsv,
}: {
  currentYear: number;
  isCsv?: boolean;
}) => ({
  policyLevel1: t('policy-level-2'),
  policyLevel2: t('policy-level-3'),
  projectNumber: t('project-number'),
  name: t('project-name'),
  parentOrganization: t('organization-in-charge'),
  startYear: t('start-fiscal-year-of-project'),
  endYear: t('end-fiscal-year-of-project'),
  accountTitle: t('accounting-type'),
  majorExpenditureBudgetPurposeParent: t(
    'report-organizing-table-format5-header-major-expenditure-budget-purpose-parent',
  ),
  majorExpenditureBudgetPurpose: t(
    'report-organizing-table-format5-header-major-expenditure-budget-purpose',
  ),
  priorUsableFinalizedAmountTotal: t('fiscal-year-with-value', {
    year: currentYear - 1,
  }),
  executableAmount: isCsv
    ? t('budgetary-sum-csv', { year: currentYear - 1 })
    : t('budgetary-sum'),
  executionAmount: isCsv
    ? t('execution-amount-csv', { year: currentYear - 1 })
    : t('execution-amount'),
  initialBudgetTotal: t('initial-budget-total', { year: currentYear }),
  skipAdministrativeReformTeamEvaluationReason: t(
    'report-organizing-table-format5-header-skip-administrative-reform-team-evaluation-reason',
  ),
  projectDetailPageUrl: t('project-detail-page-url'),
});

export const format5ValueConverter = {
  accountingType: (
    type: ReportSummaryType['accountingType'],
    accountTitle: ReportSummaryType['accountTitle'],
  ) =>
    type === 'special'
      ? (accountTitle ?? SPACE_TEXT)
      : AccountingTypeLabelModifier[accountingType.Values.general],
  previousExecutionAmountTotal: (
    params: MoneyConvertParams & {
      isDebugMode: boolean;
      negativeCount: number | null;
      rawValue?: number | null;
    },
  ) =>
    formatAmountReport(params.value, params.type, {
      negativeCount: params.negativeCount,
      isDebugMode: params.isDebugMode,
      rawValue: params.rawValue,
    }),
  previousFinalizedAmountTotal: (
    params: MoneyConvertParams & {
      isDebugMode: boolean;
      negativeCount: number | null;
      rawValue?: number | null;
    },
  ) =>
    formatAmountReport(params.value, params.type, {
      negativeCount: params.negativeCount,
      isDebugMode: params.isDebugMode,
      rawValue: params.rawValue,
    }),
  skipAdministrativeReformTeamEvaluationReason: (
    value: z.infer<typeof skipAdministrativeReformTeamEvaluationReason>,
  ) => SkipAdministrativeReformTeamEvaluationReasonLabelModifier[value],
  initialBudgetTotal: (
    params: MoneyConvertParams & {
      isDebugMode: boolean;
      negativeCount: number | null;
      rawValue?: number | null;
    },
  ) =>
    formatAmountReport(params.value, params.type, {
      negativeCount: params.negativeCount,
      isDebugMode: params.isDebugMode,
      rawValue: params.rawValue,
    }),
  summaryInitialBudgetTotal: ({ fileType, values }: SummaryConvertParams) => {
    return formatAmountReport(
      values.skipAdministrativeReformTeamEvaluationInitialBudgetTotal,
      fileType,
      fileType === 'pdf'
        ? {
            rawValue:
              values.skipAdministrativeReformTeamEvaluationRawInitialBudgetTotal,
          }
        : undefined,
    );
  },
  summaryPriorFinalizedAmountTotal: ({
    fileType,
    values,
  }: SummaryConvertParams) => {
    return formatAmountReport(
      values.skipAdministrativeReformTeamEvaluationPreviousFinalizedAmountTotal,
      fileType,
      fileType === 'pdf'
        ? {
            rawValue:
              values.skipAdministrativeReformTeamEvaluationRawPreviousFinalizedAmountTotal,
          }
        : undefined,
    );
  },
  summaryPriorExecutionAmountTotal: ({
    fileType,
    values,
  }: SummaryConvertParams) => {
    return formatAmountReport(
      values.skipAdministrativeReformTeamEvaluationPreviousExecutionAmountTotal,
      fileType,
      fileType === 'pdf'
        ? {
            rawValue:
              values.skipAdministrativeReformTeamEvaluationRawPreviousExecutionAmountTotal,
          }
        : undefined,
    );
  },
};

export const getFormat5CsvData = ({
  year,
  reports,
  isDebugMode,
  projectDetailUrlType,
}: {
  year: number;
  reports: ReportDetailsType;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}) => {
  const formattedReports = getFormattedReports({
    reports,
    isDebugMode,
    projectDetailUrlType,
  });

  return createCsv({
    headerKeys: format5ColumnType.options,
    headerConverter: format5HeaderModifier({ currentYear: year, isCsv: true }),
    body: formattedReports,
  });
};

const getFormattedReports = ({
  reports,
  isDebugMode,
  projectDetailUrlType,
}: {
  reports: ReportDetailsType;
  isDebugMode: boolean;
  projectDetailUrlType?: z.infer<typeof projectDetailUrlTypeEnum> | null;
}) => {
  return reports.policies.flatMap((policyLevel1) => {
    return policyLevel1.policyMeasuresData.flatMap((policyLevel2) => {
      return policyLevel2.projectsData.flatMap((projectData) => ({
        policyLevel1: policyLevel1.policyName ?? t('not-set'),
        policyLevel2: policyLevel2.measureName ?? t('not-set'),
        projectNumber: projectData.projectNumber,
        name: projectData.name,
        parentOrganization: projectData.parentOrganization,
        startYear: projectData.startYear ?? t('unknown-start'),
        endYear: projectData.endYear ?? t('no-scheduled-end'),
        accountTitle: format5ValueConverter.accountingType(
          projectData.accountingType,
          projectData.accountTitle,
        ),
        majorExpenditureBudgetPurposeParent:
          projectData.majorExpenditureBudgetPurposeParent,
        majorExpenditureBudgetPurpose:
          projectData.majorExpenditureBudgetPurpose,
        executableAmount: format5ValueConverter.previousFinalizedAmountTotal({
          value: projectData.previousFinalizedAmountTotal,
          type: 'csv',
          isDebugMode,
          negativeCount: projectData.negativePreviousFinalizedAmountCount,
        }),
        executionAmount: format5ValueConverter.previousExecutionAmountTotal({
          value: projectData.previousExecutionAmountTotal,
          type: 'csv',
          isDebugMode,
          negativeCount: projectData.negativePreviousYearExecutionAmountCount,
        }),
        initialBudgetTotal: format5ValueConverter.initialBudgetTotal({
          value: projectData.initialBudgetTotal,
          type: 'csv',
          isDebugMode,
          negativeCount: projectData.negativeInitialBudgetCount,
        }),
        skipAdministrativeReformTeamEvaluationReason:
          format5ValueConverter.skipAdministrativeReformTeamEvaluationReason(
            projectData.skipAdministrativeReformTeamEvaluationReason,
          ),
        projectDetailPageUrl: getProjectDetailPageUrl({
          isPS: projectDetailUrlType === 'public',
          projectId: projectData.id,
        }),
      }));
    });
  });
};
